import {
    MutationFunction,
    QueryClient,
    QueryClientProvider,
    QueryKey,
    UseMutationOptions,
    UseQueryOptions, // eslint-disable-next-line no-restricted-imports
    useMutation as _useMutation, // eslint-disable-next-line no-restricted-imports
    useQuery as _useQuery, // eslint-disable-next-line no-restricted-imports
    useQueryClient,
} from '@tanstack/react-query';
import { FC, PropsWithChildren, useMemo } from 'react';

import toast, { TToastPromiseMessageParameters } from '@components/toast';

const defaultStaleMinutes = 5;
export const defaultQueryClientOptions = {
    queries: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        staleTime: 1000 * 60 * defaultStaleMinutes,
    },
};

// eslint-disable-next-line no-restricted-imports
export { useQueries, useQueryClient } from '@tanstack/react-query';

export type GetCacheKeyFn = (...arg: [string, ...string[]]) => string | string[];

// Helper to normalize query keys - handles both string and array formats
const normalizeQueryKey = (key: QueryKey | string): QueryKey => {
    return Array.isArray(key) ? key : [key];
};

// Enhanced query client helper functions that handle both string and array keys
export const invalidateQueriesSafe = (queryClient: QueryClient, key: QueryKey | string) => {
    return queryClient.invalidateQueries({ queryKey: normalizeQueryKey(key) });
};

/**
 * Invalidates queries and forces an immediate refetch, even when no component is observing the query.
 *
 * Use this when invalidating from a location where the target query isn't mounted (e.g., after
 * saving in a wizard before navigating back to a list view). Standard invalidation only marks
 * queries as stale; with `refetchOnMount: false` (our default), stale queries won't refetch
 * on mount. This function ensures data is fresh when the user navigates back.
 */
export const forceInvalidateQueries = (queryClient: QueryClient, key: QueryKey | string) => {
    return queryClient.invalidateQueries({
        queryKey: normalizeQueryKey(key),
        refetchType: 'all',
    });
};

export const refetchQueriesSafe = (queryClient: QueryClient, key: QueryKey | string) => {
    return queryClient.refetchQueries({ queryKey: normalizeQueryKey(key) });
};

export const removeQueriesSafe = (queryClient: QueryClient, key: QueryKey | string) => {
    return queryClient.removeQueries({ queryKey: normalizeQueryKey(key) });
};

export const cancelQueriesSafe = (queryClient: QueryClient, key: QueryKey | string) => {
    return queryClient.cancelQueries({ queryKey: normalizeQueryKey(key) });
};

export const getQueryDataSafe = <T = unknown,>(
    queryClient: QueryClient,
    key: QueryKey | string,
): T | undefined => {
    return queryClient.getQueryData<T>(normalizeQueryKey(key));
};

export const setQueryDataSafe = <T = unknown,>(
    queryClient: QueryClient,
    key: QueryKey | string,
    data: T | ((old: T | undefined) => T),
) => {
    return queryClient.setQueryData(normalizeQueryKey(key), data);
};

export function useAdvancedQuery<IResponse, IError>(
    queryKey: QueryKey | string, // Accept both string and array
    queryFn: () => Promise<IResponse>,
    customOptions: UseQueryOptions<IResponse, IError> = {},
) {
    // Normalize query key to array format for v4 compatibility
    const normalizedQueryKey = normalizeQueryKey(queryKey);

    const isKeyDisabled = useMemo(() => {
        return normalizedQueryKey.some((dep) => dep == null);
    }, [normalizedQueryKey]);

    // `customOptions.enabled` should take precedence if provided
    const finalEnabled = customOptions.enabled ?? !isKeyDisabled;

    const query = _useQuery<IResponse, IError>({
        queryKey: normalizedQueryKey, // Always pass array to useQuery
        queryFn,
        staleTime: 60 * 60 * 1000,
        enabled: finalEnabled,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        retry: false,
        keepPreviousData: true,
        placeholderData: (prev: IResponse | undefined) => prev as IResponse,
        useErrorBoundary: false,
        ...customOptions,
    } as UseQueryOptions<IResponse, IError>);

    // Add normalized flags so callers don't have to remember v4 semantics
    const safeIsLoading = finalEnabled ? query.isLoading : false;
    const safeIsReady = finalEnabled ? query.isFetched : true;

    return { ...query, isLoading: safeIsLoading, isReady: safeIsReady, enabled: finalEnabled };
}

interface IMutationOptions<TQueryObjectType, TPayload, TResponse, TError, TContext> {
    updateQuery?: {
        queryKey: QueryKey;
        useOptimistic?: boolean;
    };
    updateListQuery?: {
        queryKey: QueryKey;
        findFn: (item: TQueryObjectType) => boolean;
        useOptimistic?: boolean;
    };
    toastOptions?: TToastPromiseMessageParameters;
    customOptions?: UseMutationOptions<TResponse, TError, TPayload, TContext>;
}

const buildMutationCallbackFnStack = <TResponse, TPayload>(
    key: string,
    obj: any,
    newFn: (payload: TPayload) => TResponse,
) => {
    const fns: ((payload: TPayload) => TResponse)[] = [newFn];

    if (obj?.[key]) {
        fns.push(obj[key]);
    }

    return (payload: TPayload) => {
        return fns.reduce((res, fn) => {
            return {
                ...res,
                ...(fn(payload) || {}),
            };
        }, {}) as TResponse;
    };
};

function applyToast<TResponse, TPayload>(
    mutationFn: MutationFunction<TResponse, TPayload>,
    toastOptions: TToastPromiseMessageParameters,
): MutationFunction<TResponse, TPayload> {
    return (variables: TPayload): Promise<TResponse> => {
        const promise = mutationFn(variables);
        toast.promise(promise, toastOptions);
        return promise;
    };
}

export function useAdvancedMutation<TQueryObjectType, TPayload, TResponse, TError, TContext>(
    mutationFn: MutationFunction<TResponse, TPayload>,
    options: IMutationOptions<TQueryObjectType, TPayload, TResponse, TError, TContext> = {},
) {
    const queryClient = useQueryClient();

    const { updateQuery, updateListQuery, toastOptions, customOptions = {} } = options;

    /**
     * Using `updateQuery.queryKey`, will invalidate the item in specified query.
     * If `useOptimistic` is set, the existing query data is updated with the payload
     *
     * TODO: Abstract into generic fn (?) since very similar to `updateListQuery.queryKey`
     */
    if (updateQuery?.queryKey) {
        const { queryKey, useOptimistic } = updateQuery;

        if (useOptimistic) {
            let prevValue: TQueryObjectType | undefined;

            customOptions.onMutate = buildMutationCallbackFnStack<TContext, TPayload>(
                'onMutate',
                customOptions,
                (payload) => {
                    prevValue = getQueryDataSafe<TQueryObjectType>(queryClient, queryKey);

                    setQueryDataSafe(queryClient, queryKey, {
                        ...prevValue,
                        ...payload,
                    });

                    return {} as TContext;
                },
            );

            customOptions.onError = buildMutationCallbackFnStack<void, TError>(
                'onError',
                customOptions,
                () => {
                    setQueryDataSafe(queryClient, queryKey, prevValue);
                },
            );
        }

        customOptions.onSettled = buildMutationCallbackFnStack('onSettled', customOptions, () => {
            invalidateQueriesSafe(queryClient, queryKey);
        });
    }

    /**
     * Using `updateListQuery.queryKey`, will invalidate the item in specified list query.
     * If `useOptimistic` is set, the `findFn` will find the item in the list query
     * and update the object with the payload
     *
     * TODO: Abstract into generic fn (?) since very similar to `updateQuery.queryKey`
     */
    if (updateListQuery?.queryKey) {
        const { queryKey, findFn, useOptimistic } = updateListQuery;

        if (useOptimistic) {
            let prevValue: TQueryObjectType[] | undefined;

            customOptions.onMutate = buildMutationCallbackFnStack<TContext, TPayload>(
                'onMutate',
                customOptions,
                (payload) => {
                    prevValue = getQueryDataSafe<TQueryObjectType[]>(queryClient, queryKey);

                    if (prevValue) {
                        const i = prevValue.findIndex(findFn);

                        if (i > -1) {
                            prevValue[i] = {
                                ...prevValue[i],
                                ...payload,
                            };

                            setQueryDataSafe(queryClient, queryKey, prevValue);
                        }
                    }

                    return {} as TContext;
                },
            );

            customOptions.onError = buildMutationCallbackFnStack<void, TError>(
                'onError',
                customOptions,
                () => {
                    setQueryDataSafe(queryClient, queryKey, prevValue);
                },
            );
        }

        customOptions.onSettled = buildMutationCallbackFnStack('onSettled', customOptions, () => {
            invalidateQueriesSafe(queryClient, queryKey);
        });
    }

    return _useMutation<TResponse, TError, TPayload, TContext>({
        mutationFn: toastOptions ? applyToast(mutationFn, toastOptions) : mutationFn,
        ...customOptions,
    });
}

// Unit testing utility
export const createReactQueryTestWrapper = (): FC<PropsWithChildren> => {
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                // Inherit all of the default options used in the app
                ...defaultQueryClientOptions.queries,
                // ✅ turns retries off
                retry: false,
            },
        },
    });

    return ({ children }) => (
        <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    );
};
